import { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, X, ImageIcon, Loader2 } from 'lucide-react';
import apiClient from '@/services/api/client';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface Props {
  value: string | null | undefined;
  onChange: (url: string | null) => void;
  label?: string;
  aspect?: 'wide' | 'square' | 'portrait';
  /** Path under which the upload-url is requested. Defaults to workshops. */
  uploadEndpoint?: string;
  className?: string;
}

const ASPECTS: Record<NonNullable<Props['aspect']>, string> = {
  wide: 'aspect-[16/9]',
  square: 'aspect-square',
  portrait: 'aspect-[3/4]',
};

export function ImageUpload({
  value, onChange, label, aspect = 'wide',
  uploadEndpoint = '/admin/workshops/upload-url',
  className,
}: Props) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image is larger than 8 MB — please pick a smaller one');
      return;
    }
    setUploading(true);
    try {
      // Step 1 — get a presigned PUT URL
      const presign = await apiClient.post<{ data: { uploadUrl: string; publicUrl: string } }>(
        uploadEndpoint,
        { filename: file.name, contentType: file.type }
      );
      const { uploadUrl, publicUrl } = presign.data.data;

      // Step 2 — PUT the file directly to S3 (no auth header here)
      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': file.type },
        // Skip the global apiClient interceptors by using bare axios
      });

      onChange(publicUrl);
      toast.success('Image uploaded');
    } catch (e: any) {
      console.error('[ImageUpload]', e);
      toast.error(e?.response?.data?.error || e?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-medium text-muted-foreground mb-1.5">{label}</label>
      )}
      <div
        className={cn(
          'relative overflow-hidden rounded-xl border border-dashed border-border bg-muted/30',
          ASPECTS[aspect],
        )}
      >
        {value ? (
          <>
            <img src={value} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 hover:opacity-100">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="rounded-full bg-white text-stone-900 px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 shadow-md"
              >
                <Upload className="h-3.5 w-3.5" /> Replace
              </button>
              <button
                type="button"
                onClick={() => onChange(null)}
                disabled={uploading}
                className="rounded-full bg-rose-600 text-white px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 shadow-md"
              >
                <X className="h-3.5 w-3.5" /> Remove
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
          >
            {uploading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-xs">Uploading…</span>
              </>
            ) : (
              <>
                <ImageIcon className="h-5 w-5" />
                <span className="text-xs font-medium">Click to upload</span>
                <span className="text-[10px] text-muted-foreground/70">PNG, JPG up to 8 MB</span>
              </>
            )}
          </button>
        )}
        {uploading && value && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-white animate-spin" />
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
    </div>
  );
}
